/**
 * SnakeTimeline — the curved journey (spec §84–§89).
 *
 * The path is constructed *through* the node centres on a serpentine grid, so
 * it passes exactly through every node by construction rather than by
 * measurement.
 *
 * The change that makes it usable is in layout.ts: **node spacing is fixed**.
 * The first version divided the container width by the column count, so three
 * nodes on a wide screen sat 800px apart with nothing between them. Now a wider
 * screen shows more nodes per row instead, and density stays readable at any
 * width and any event count.
 *
 * Everything else here is in service of reading it:
 *   · every date carries its year — these journeys span 2025 and 2026
 *   · a dark marker sits on the segment where the calendar year turns
 *   · labels are compact cards anchored to their own node, alternating above
 *     and below so they never overlap the path or each other
 *   · hovering shows detail beside the cursor; clicking pins the full record
 *   · a gap of two days or more goes dashed and carries the day count, so
 *     conversion velocity is visible rather than something to work out
 *   · a sequence number under each node keeps reading order unambiguous
 */

import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from "react";

import {
  buildLayout, LABEL_HALF_WIDTH, type LayoutPoint,
} from "@/components/timeline/layout";
import { eventMeta } from "@/components/timeline/typeMeta";
import type { JourneyEvent } from "@/types/api";
import { inr } from "@/utils/format";

/** Inline icon paths. Never emoji (§89). */
const ICON_PATH: Record<string, string> = {
  client: "M12 4.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8Z M5.5 20.5a6.5 6.5 0 0 1 13 0",
  activity: "M19 10.5c0 5.5-7 11-7 11s-7-5.5-7-11a7 7 0 0 1 14 0Z M12 7.9a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8Z",
  followup: "M20.5 12a8.5 8.5 0 1 1-2.9-6.4 M20.5 3.5V9H15",
  response: "M20.5 11.3a8 8 0 1 1-4.4-7.1 M4.2 19.8 3 22l3.4-1.1 M9 11h6",
  commitment: "m2.5 12.5 4.5 4.5 6.5-6.5 M10.5 12.5l2.5 2.5 8-8.5",
  lead: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z M12 10.6a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8Z",
  admission: "M4 21V8l8-5 8 5v13 M10 21v-5h4v5 M12 8.5v3.5 M10.2 10.2h3.6",
  billing: "M5 3v18l2.3-1.4L9.7 21l2.3-1.4L14.3 21l2.3-1.4L19 21V3l-2.3 1.4L14.3 3l-2.3 1.4L9.7 3 7.3 4.4Z M9 9h6 M9 13h6",
  referral: "M6.5 9.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2Z M17.5 3.9a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2Z M17.5 14.9a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2Z m8.9 10.8 6.2-3.1 M8.9 13.2l6.2 3.1",
  conversion: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z m8 9.2 2.6 2.6L16 9.4",
  revenue: "M7 4h10 M7 8.5h10 M14.5 20 8 12.5h2.6a4 4 0 0 0 0-8",
};

const dayMonth = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
const yearOf = (iso: string) => new Date(iso).getFullYear();
const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

export interface SnakeTimelineProps {
  events: JourneyEvent[];
  mode?: "client" | "lead" | "employee" | "conversion" | "referral";
  animated?: boolean;
  onSelect?: (event: JourneyEvent) => void;
}

/** Stable no-op so the memoised node does not re-render on every parent pass. */
const NO_HOVER = () => {};

export function SnakeTimeline({
  events, mode = "client", animated = true, onSelect,
}: SnakeTimelineProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1000);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hovered, setHovered] =
    useState<{ event: JourneyEvent; x: number; y: number } | null>(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  // Touch devices report `hover: none`. Without this the cursor-following card
  // is shown on tap by the synthetic mouseenter, then never cleared — there is
  // no mouseleave to come. On those devices a tap goes straight to the pinned
  // panel, which has a Close button and does not depend on a pointer staying
  // still.
  const [hasHover, setHasHover] = useState(true);

  useEffect(() => {
    const query = window.matchMedia("(hover: hover) and (pointer: fine)");
    const apply = () => setHasHover(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(query.matches);
    const listener = () => setReduceMotion(query.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, []);

  useLayoutEffect(() => {
    const node = shellRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => setWidth(node.clientWidth || 1000));
    observer.observe(node);
    setWidth(node.clientWidth || 1000);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => buildLayout(events, width), [events, width]);
  const selected = useMemo(
    () => events.find((e) => e.id === selectedId) ?? null,
    [events, selectedId],
  );

  const pick = useCallback((event: JourneyEvent) => {
    setSelectedId((current) => (current === event.id ? null : event.id));
    onSelect?.(event);
  }, [onSelect]);

  const onKeyDown = useCallback(
    (index: number) => (keyEvent: React.KeyboardEvent<SVGGElement>) => {
      if (keyEvent.key === "Enter" || keyEvent.key === " ") {
        keyEvent.preventDefault();
        const target = events[index];
        if (target) pick(target);
        return;
      }
      const step = keyEvent.key === "ArrowRight" ? 1
        : keyEvent.key === "ArrowLeft" ? -1 : 0;
      if (step === 0) return;
      keyEvent.preventDefault();
      const next = events[index + step];
      if (!next) return;
      setSelectedId(next.id);
      const nodes = shellRef.current?.querySelectorAll<SVGGElement>("[data-snake-node]");
      nodes?.[index + step]?.focus();
    },
    [events, pick],
  );

  if (events.length === 0) {
    return (
      <div className="card p-10 text-center">
        <p className="text-sm text-ink-muted">No journey events to plot.</p>
      </div>
    );
  }

  const labelWidth = Math.min(LABEL_HALF_WIDTH * 2, (layout.spacing || 200) - 14);

  return (
    <div className="card overflow-hidden relative" ref={shellRef}>
      <svg
        viewBox={`0 0 ${width} ${layout.height}`}
        height={layout.height}
        className="block w-full h-auto"
        role="img"
        aria-label={`${mode} journey, ${events.length} events`}
        onMouseLeave={() => setHovered(null)}
      >
        {layout.segments.map((segment, index) => {
          const meta = eventMeta(segment.to.event);
          const dashed = segment.gapDays >= 2;
          return (
            <g key={`seg-${index}`}>
              <path
                d={segment.d}
                fill="none"
                stroke={meta.color}
                strokeWidth={2.5}
                strokeLinecap="round"
                opacity={dashed ? 0.36 : 0.5}
                strokeDasharray={dashed ? "6 6" : undefined}
                style={animated && !reduceMotion && !dashed
                  ? { animation: `snake-draw .4s ease ${Math.min(index * 0.015, 1)}s both` }
                  : undefined}
              />
              {dashed && (
                <g transform={`translate(${segment.midX}, ${segment.isArc ? segment.midY : segment.midY - 12})`}>
                  <rect x={-20} y={-8} width={40} height={16} rx={8}
                        fill="#FFFFFF" stroke="#DBE3E0" />
                  <text textAnchor="middle" y={4} fontSize={9.5}
                        className="font-mono" fill="#8B9B96">
                    {segment.gapDays}d
                  </text>
                </g>
              )}
              {segment.yearBoundary !== null && (
                <g transform={`translate(${segment.midX}, ${segment.isArc ? segment.midY : segment.midY + 16})`}>
                  <rect x={-27} y={-9} width={54} height={18} rx={4} fill="#16211F" />
                  <text textAnchor="middle" y={4.5} fontSize={10.5}
                        className="font-mono" fill="#FFFFFF" letterSpacing="0.06em">
                    {segment.yearBoundary}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {layout.points.map((point, index) => (
          <SnakeNode
            key={point.event.id}
            point={point}
            index={index}
            selected={selectedId === point.event.id}
            labelWidth={labelWidth}
            onPick={pick}
            onKeyDown={onKeyDown(index)}
            onHover={hasHover ? setHovered : NO_HOVER}
          />
        ))}
      </svg>

      {/* Screen readers get the sequence in DOM order; the SVG is the picture. */}
      <ol className="sr-only">
        {events.map((event) => (
          <li key={`sr-${event.id}`}>
            {new Date(event.timestamp).toLocaleString("en-IN")} — {event.title}.{" "}
            {event.description}
          </li>
        ))}
      </ol>

      {hasHover && hovered && !selected && (
        <HoverCard event={hovered.event} x={hovered.x} y={hovered.y} />
      )}
      {selected && (
        <PinnedDetail event={selected} onClose={() => setSelectedId(null)} />
      )}

      <style>{`
        @keyframes snake-draw {
          from { stroke-dasharray: 900; stroke-dashoffset: 900; }
          to   { stroke-dasharray: 900; stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}

function SnakeNode({
  point, index, selected, labelWidth, onPick, onKeyDown, onHover,
}: {
  point: LayoutPoint<JourneyEvent>;
  index: number;
  selected: boolean;
  labelWidth: number;
  onPick: (e: JourneyEvent) => void;
  onKeyDown: (e: React.KeyboardEvent<SVGGElement>) => void;
  onHover: (v: { event: JourneyEvent; x: number; y: number } | null) => void;
}) {
  const { event } = point;
  const meta = eventMeta(event);
  const inferred = event.linkConfidence === "inferred";
  // Alternate the label side by column so neighbouring cards never collide.
  const above = point.col % 2 === 0;
  const labelTop = above ? point.y - 78 : point.y + 26;
  const maxChars = Math.max(10, Math.floor(labelWidth / 6.6));

  return (
    <g
      data-snake-node
      tabIndex={0}
      role="button"
      aria-label={`${event.title}, ${dayMonth(event.timestamp)} ${yearOf(event.timestamp)}`}
      className="cursor-pointer focus:outline-none"
      onClick={() => onPick(event)}
      onKeyDown={onKeyDown}
      onMouseEnter={(e) => onHover({ event, x: e.clientX, y: e.clientY })}
      onMouseMove={(e) => onHover({ event, x: e.clientX, y: e.clientY })}
      // Clear on the node itself, not just on the svg: leaving a node for empty
      // canvas is the common case, and it used to leave the card stranded.
      onMouseLeave={() => onHover(null)}
      onBlur={() => onHover(null)}
    >
      {/* Hit area: the node and its label card are one hover target, so moving
          between them does not flicker the tooltip off and on. */}
      <circle cx={point.x} cy={point.y} r={26} fill="transparent" />
      <rect
        x={point.x - labelWidth / 2} y={labelTop} width={labelWidth} height={52}
        fill="transparent"
      />

      <g transform={`translate(${point.x}, ${labelTop})`}>
        <rect
          x={-labelWidth / 2} y={0} width={labelWidth} height={52} rx={6}
          fill={selected ? meta.bg : "#FFFFFF"}
          stroke={selected ? meta.color : "#DBE3E0"}
          strokeWidth={selected ? 1.6 : 1}
        />
        <text x={0} y={16} textAnchor="middle" fontSize={9.5}
              className="font-mono" fill="#8B9B96" letterSpacing="0.05em">
          {dayMonth(event.timestamp).toUpperCase()} {yearOf(event.timestamp)}
        </text>
        <text x={0} y={31} textAnchor="middle" fontSize={12}
              className="font-display" fontWeight={600} fill="#16211F">
          {truncate(event.title, maxChars)}
        </text>
        <text x={0} y={44} textAnchor="middle" fontSize={10}
              fill={meta.color} className="font-mono">
          {event.value != null
            ? inr(event.value)
            : truncate(event.description || meta.label, maxChars + 4)}
        </text>
      </g>

      {/* Drawn after the label so the node always sits on top of the connector. */}
      <circle
        cx={point.x} cy={point.y} r={17}
        fill="#FFFFFF"
        stroke={meta.color}
        strokeWidth={selected ? 3 : 1.8}
        strokeDasharray={inferred ? "3.5 3" : undefined}
      />
      <g
        transform={`translate(${point.x - 8},${point.y - 8}) scale(0.667)`}
        stroke={meta.color} strokeWidth={2.4} fill="none"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <path d={ICON_PATH[event.entityType] ?? ICON_PATH.activity!} />
      </g>
      <text x={point.x} y={point.y + 31} textAnchor="middle" fontSize={8.5}
            className="font-mono" fill="#8B9B96">
        {index + 1}
      </text>
    </g>
  );
}

/** Follows the cursor. Deliberately small — the pinned panel carries the rest. */
function HoverCard({ event, x, y }: { event: JourneyEvent; x: number; y: number }) {
  const meta = eventMeta(event);
  const entries = Object.entries(event.metadata ?? {}).slice(0, 7);
  const flipLeft = x > window.innerWidth - 340;
  const flipUp = y > window.innerHeight - 300;

  return (
    <div
      className="fixed z-50 w-[312px] card p-3 pointer-events-none"
      style={{
        left: flipLeft ? x - 324 : x + 16,
        top: flipUp ? Math.max(8, y - 270) : y + 14,
        boxShadow: "0 12px 32px -12px rgba(22,33,31,.35)",
      }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="chip border-transparent"
              style={{ color: meta.color, background: meta.bg }}>
          {meta.label}
        </span>
        <span className="font-mono text-[10.5px] text-ink-faint">
          {new Date(event.timestamp).toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          })}
        </span>
      </div>
      <div className="font-display font-semibold text-[14px]">{event.title}</div>
      {event.description && (
        <div className="text-[12.5px] text-ink-muted">{event.description}</div>
      )}
      <dl className="mt-2 space-y-0.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 text-[11.5px]">
            <dt className="text-ink-faint shrink-0">{k}</dt>
            <dd className="text-ink font-medium text-right truncate">{String(v ?? "—")}</dd>
          </div>
        ))}
      </dl>
      <div className="text-[10.5px] text-ink-faint mt-2 pt-1.5 border-t border-rule">
        click to pin every field
      </div>
    </div>
  );
}

function PinnedDetail({ event, onClose }: { event: JourneyEvent; onClose: () => void }) {
  const meta = eventMeta(event);
  return (
    <div className="sticky bottom-0 border-t-2 bg-surface px-4 py-3"
         style={{ borderColor: meta.color }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="chip border-transparent"
                  style={{ color: meta.color, background: meta.bg }}>
              {meta.label}
            </span>
            {event.linkConfidence === "inferred" && (
              <span className="chip border-warn/30 bg-warn-soft text-warn">
                inferred link
              </span>
            )}
            <span className="font-mono text-[11px] text-ink-faint">
              {new Date(event.timestamp).toLocaleString("en-IN", {
                day: "2-digit", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })} · Asia/Kolkata
            </span>
          </div>
          <h4 className="font-display font-semibold text-[17px] mt-0.5">{event.title}</h4>
          <p className="text-[13px] text-ink-muted">{event.description}</p>
          {event.sourceTable && (
            <p className="font-mono text-[11px] text-ink-faint mt-0.5">
              {event.sourceTable} · {event.sourceId}
            </p>
          )}
        </div>
        <button className="btn text-xs shrink-0" onClick={onClose}>Close</button>
      </div>
      <dl className="grid gap-x-6 gap-y-1.5 mt-3"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
        {Object.entries(event.metadata ?? {}).map(([k, v]) => (
          <div key={k}
               className="flex justify-between gap-3 text-[12.5px] border-t border-rule pt-1">
            <dt className="text-ink-faint shrink-0">{k}</dt>
            <dd className="text-ink font-medium text-right break-words">
              {String(v ?? "—")}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
